import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ExternalLinkIcon } from "../ui/external-link-icon";
import { externalLinkProps, isExternalLink } from "../../lib/external-link";
import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { DEFAULT_SITE_SETTINGS, SOCIAL_PLATFORM_LABELS, } from "@diality/contracts";
import { SocialIcon } from "../ui/social-icon";
import { DialityBrand } from "../ui/diality-brand";
import { NavRefract, navArchMask, INITIAL_NAV_ARCH_SIZE, } from "../ui/nav-refract";
const rowBaseClass = "fixed left-1/2 top-[-10px] flex h-[94px] w-max -translate-x-1/2 items-center justify-center gap-12 pt-[10px] font-sans text-[18px] leading-[22px] font-medium";
const rowClass = `${rowBaseClass} max-[1161px]:hidden`;
/**
 * ── VIEWPORT vs FRAME ──────────────────────────────────────────────────────
 * Every breakpoint below is written twice: once against the viewport, for the
 * live site, and once as a container query against InlineNavFrame, for an
 * inline bar. An inline bar's width is its FRAME's width — the canvas device
 * presets are 834px and 390px frames inside a wide window — so viewport
 * variants leave a 390px frame rendering the 1232px desktop bar, with the links
 * piled on top of the logo. Both spellings are written out in full and never
 * composed at runtime: Tailwind reads class names as literal source strings.
 */
const rowInlineClass = `${rowBaseClass} @max-[1161px]/navbar:hidden`;
const barResponsiveClass = "max-[1161px]:w-full max-[1161px]:transition-none max-[481px]:top-0 max-[481px]:h-[84px] max-[481px]:rounded-none! max-[481px]:px-[43px] max-[481px]:py-[26px] max-[481px]:[mask:none]! max-[481px]:[-webkit-mask:none]! max-[481px]:[backdrop-filter:blur(7px)_saturate(1.2)]! max-[481px]:[-webkit-backdrop-filter:blur(7px)_saturate(1.2)]!";
const barResponsiveInlineClass = "@max-[1161px]/navbar:w-full @max-[1161px]/navbar:transition-none @max-[481px]/navbar:top-0 @max-[481px]/navbar:h-[84px] @max-[481px]/navbar:rounded-none! @max-[481px]/navbar:px-[43px] @max-[481px]/navbar:py-[26px] @max-[481px]/navbar:[mask:none]! @max-[481px]/navbar:[-webkit-mask:none]! @max-[481px]/navbar:[backdrop-filter:blur(7px)_saturate(1.2)]! @max-[481px]/navbar:[-webkit-backdrop-filter:blur(7px)_saturate(1.2)]!";
const focusClass = "focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white";
const ctaClass = "min-w-[124px] border-white px-5 py-0 font-medium text-white group-hover/button:border-white group-hover/button:bg-white/10 group-hover/button:text-white motion-reduce:transition-none";
/**
 * The frame an inline bar sits in: 84px of real height (94 less the arch's
 * 10px lead-in), so the content below starts where the bar visually ends.
 *
 * The arch mask starts with a 10px square-shouldered lead-in at each end
 * (NAV_ARCH_TOP_LEAD), with a visible rim stroke along it. Fixed, the bar hangs
 * at `top: -10px` and the viewport edge cuts that lead-in off — which is the
 * only reason the shape reads as an arch rather than a tab with two exposed
 * corners. Inline, that cut is a `clip-path` on each layer rather than
 * `overflow-hidden` here: an arch shadow has to be able to leave the frame
 * downwards while the lead-in is still trimmed upwards, and one `overflow`
 * cannot do both. `clipTopLead` is that cut; the shadow layer uses the same one
 * with a negative bottom inset so its own shadow escapes.
 */
/* LITERAL, never interpolated from NAV_ARCH_TOP_LEAD: Tailwind reads class
 * names as source text, so a composed one generates no rule at all. The 10px
 * here IS that constant — keep them in step by hand. */
const clipTopLead = "[clip-path:inset(10px_0_0_0)]";
function InlineNavFrame({ children }) {
    return (_jsx("div", { className: "@container/navbar relative isolate mx-auto h-[84px] w-full", children: children }));
}
/**
 * Prototype navigation: an arched backdrop lens, a separate moving pill and
 * crisp labels above both. Routing stays with the consuming application.
 */
const MarketingNav = React.forwardRef(({ className, style, brand = _jsx(DialityBrand, { wordmarkClassName: "text-white" }), brandHref = "/", links, actions, contactHref = "/contact", cta = { label: "Contact", href: contactHref }, homeLabel = "Home", linkAs: LinkAs = "a", menuFooter, socialLinks, layout = "fixed", archShadow, onMouseEnter, onMouseLeave, onFocusCapture, onBlurCapture, ...props }, forwardedRef) => {
    const inline = layout === "inline";
    const [open, setOpen] = React.useState(false);
    const [linksOverflow, setLinksOverflow] = React.useState(false);
    const [desktopWidth, setDesktopWidth] = React.useState(1232);
    const [compact, setCompact] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);
    const [hovered, setHovered] = React.useState(null);
    const [pillMoves, setPillMoves] = React.useState(false);
    const [pill, setPill] = React.useState(null);
    const navRef = React.useRef(null);
    const desktopRowRef = React.useRef(null);
    const brandRef = React.useRef(null);
    const actionsRef = React.useRef(null);
    const linkRefs = React.useRef([]);
    const pointerWithin = React.useRef(false);
    const pointerPosition = React.useRef(null);
    React.useEffect(() => {
        const trackPointer = (event) => {
            pointerPosition.current = { x: event.clientX, y: event.clientY };
        };
        window.addEventListener("mousemove", trackPointer, { passive: true });
        return () => window.removeEventListener("mousemove", trackPointer);
    }, []);
    const focusWithin = React.useRef(false);
    const pillIsVisible = React.useRef(false);
    const collapsed = !linksOverflow && compact && !expanded;
    const [linksRevealed, setLinksRevealed] = React.useState(true);
    React.useEffect(() => {
        if (collapsed) {
            setLinksRevealed(false);
            return;
        }
        // The outer links finish at 500ms + their 200ms fade. Hover effects
        // must not compete with this reveal, regardless of how the bar opens.
        const timer = window.setTimeout(() => setLinksRevealed(true), 700);
        return () => window.clearTimeout(timer);
    }, [collapsed]);
    // Measure the expanded desktop design, never the animated/compact bar width.
    // Keep the hidden desktop row laid out above the static breakpoint so it can
    // recover when labels shorten or the frame grows, without oscillating modes.
    React.useLayoutEffect(() => {
        const nav = navRef.current;
        const row = desktopRowRef.current;
        const brand = brandRef.current;
        const actionsBox = actionsRef.current;
        if (!nav || !row || !brand || !actionsBox)
            return;
        const frame = inline ? nav.parentElement : document.documentElement;
        if (!frame)
            return;
        let cancelled = false;
        const measure = () => {
            if (cancelled)
                return;
            const available = frame.clientWidth;
            if (available < 1161) {
                setLinksOverflow(false);
                return;
            }
            const availableWidth = available - 48;
            const gutter = parseFloat(getComputedStyle(nav).paddingLeft);
            // Keep at least 48px between the centered labels and both the logo
            // and CTA. offsetWidth ignores CMS zoom.
            const sideWidth = Math.max(brand.offsetWidth, actionsBox.scrollWidth);
            const requiredWidth = Math.ceil(row.offsetWidth + 2 * (gutter + sideWidth + 48));
            setDesktopWidth(Math.max(1232, requiredWidth));
            setLinksOverflow(requiredWidth > availableWidth);
        };
        measure();
        const observer = new ResizeObserver(measure);
        [frame, row, brand, actionsBox].forEach((element) => observer.observe(element));
        window.addEventListener("resize", measure);
        document.fonts.addEventListener("loadingdone", measure);
        void document.fonts.ready.then(measure);
        return () => {
            cancelled = true;
            observer.disconnect();
            window.removeEventListener("resize", measure);
            document.fonts.removeEventListener("loadingdone", measure);
        };
    }, [inline, links, brand, actions, cta]);
    React.useEffect(() => {
        if (linksOverflow) {
            setCompact(false);
            setExpanded(false);
            setHovered(null);
        }
        else if (navRef.current &&
            (inline
                ? (navRef.current.parentElement?.clientWidth ?? 0) >= 1161
                : window.innerWidth >= 1161)) {
            setOpen(false);
        }
    }, [linksOverflow, inline]);
    const linksId = React.useId();
    const profiles = socialLinks ?? DEFAULT_SITE_SETTINGS.socialLinks;
    const renderedMenuFooter = menuFooter !== undefined ? (menuFooter) : profiles.length === 0 ? null : (_jsx("nav", { "data-social-profiles": true, "aria-label": "Diality social media", className: "flex items-center gap-9 flex-wrap text-white", children: profiles.map(({ platform, url }) => (_jsx("a", { "data-social-profile": platform, href: url, "aria-label": SOCIAL_PLATFORM_LABELS[platform], target: "_blank", rel: "noopener noreferrer", className: "inline-flex transition-opacity hover:opacity-70 focus-visible:opacity-70", children: _jsx(SocialIcon, { platform: platform }) }, platform))) }));
    React.useEffect(() => {
        // Compaction is a response to the WINDOW scrolling. An inline bar lives in
        // some other scroll container (the canvas scrolls an inner overflow-auto
        // div), so this would either never fire or — worse — fire off the host
        // page's own scroll and collapse the bar to its 360px "Menu" pill while
        // the canvas itself never moved.
        if (inline || linksOverflow)
            return;
        const query = window.matchMedia("(max-width: 1160px)");
        let previousY = window.scrollY;
        let upwardDistance = 0;
        let frame = 0;
        const update = () => {
            frame = 0;
            const y = Math.max(0, window.scrollY);
            const delta = y - previousY;
            previousY = y;
            const shouldCompact = !query.matches && y > 150;
            setCompact(shouldCompact);
            if (!shouldCompact) {
                upwardDistance = 0;
                setExpanded(false);
            }
            else if (pointerWithin.current || focusWithin.current) {
                setExpanded(true);
            }
            else if (delta > 0) {
                upwardDistance = 0;
                setExpanded(false);
            }
            else if (delta < 0) {
                upwardDistance += -delta;
                if (upwardDistance >= 50)
                    setExpanded(true);
            }
        };
        const scroll = () => {
            if (!frame)
                frame = window.requestAnimationFrame(update);
        };
        const resize = () => {
            setHovered(null);
            if (!query.matches)
                setOpen(false);
            update();
        };
        update();
        window.addEventListener("scroll", scroll, { passive: true });
        query.addEventListener("change", resize);
        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener("scroll", scroll);
            query.removeEventListener("change", resize);
        };
    }, [inline, linksOverflow]);
    React.useEffect(() => {
        if (collapsed || open) {
            pillIsVisible.current = false;
            setHovered(null);
        }
    }, [collapsed, open]);
    const positionPill = (index) => {
        const nav = navRef.current;
        const label = linkRefs.current[index];
        if (!nav || !label)
            return;
        const bounds = label.getBoundingClientRect();
        const bar = nav.getBoundingClientRect();
        setPill({
            left: bounds.left - 24,
            top: bar.top + (bar.height - 38) / 2 + 5,
            width: bounds.width + 48,
        });
    };
    const showPill = (index) => {
        if (links[index]?.active) {
            clearPill();
            return;
        }
        const firstHover = !pillIsVisible.current;
        pillIsVisible.current = true;
        // Commit the destination before making a first-hover pill visible. Only
        // moves between already-hovered links should animate horizontally.
        positionPill(index);
        setPillMoves(!firstHover);
        setHovered(index);
    };
    const clearPill = () => {
        pillIsVisible.current = false;
        setPillMoves(false);
        setHovered(null);
    };
    // Expanding the bar can reveal a link beneath a stationary pointer without
    // another mouse-enter event. Resolve the current target after the reveal.
    React.useLayoutEffect(() => {
        const pointer = pointerPosition.current;
        if (!linksRevealed ||
            collapsed ||
            open ||
            !pointerWithin.current ||
            !pointer)
            return;
        const index = linkRefs.current.findIndex((label) => {
            const bounds = label?.parentElement?.getBoundingClientRect();
            return (bounds &&
                pointer.x >= bounds.left &&
                pointer.x <= bounds.right &&
                pointer.y >= bounds.top &&
                pointer.y <= bounds.bottom);
        });
        if (index !== -1)
            showPill(index);
    }, [linksRevealed, collapsed, open]);
    // Observe the actual label metrics, including font loading and resizing.
    React.useLayoutEffect(() => {
        if (hovered === null || collapsed)
            return;
        const nav = navRef.current;
        const label = linkRefs.current[hovered];
        if (!nav || !label)
            return;
        const update = () => positionPill(hovered);
        update();
        const observer = new ResizeObserver(update);
        observer.observe(nav);
        observer.observe(label);
        window.addEventListener("resize", update);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [hovered, collapsed, links]);
    const close = () => {
        setOpen(false);
        clearPill();
    };
    // `actions === undefined` rather than a falsy test: passing `actions={null}`
    // is how a caller omits the CTA entirely.
    const contact = actions !== undefined ? (actions) : cta ? (_jsx(Button, { asChild: true, variant: "outline", className: cn(ctaClass, focusClass), children: _jsxs(LinkAs, { "data-nav-cta": true, href: cta.href, ...externalLinkProps(cta.href), onClick: close, children: [cta.label, isExternalLink(cta.href) && _jsx(ExternalLinkIcon, {})] }) })) : null;
    const overlayLinks = [
        ...(links.some((link) => link.href === brandHref)
            ? []
            : [{ label: homeLabel, href: brandHref }]),
        ...links,
        ...(!cta || links.some((link) => link.href === cta.href) ? [] : [cta]),
    ];
    const motionStyle = (index) => ({
        transitionDelay: collapsed
            ? `${100 + Math.min(index, links.length - 1 - index) * 50}ms`
            : `${500 - Math.min(index, links.length - 1 - index) * 50}ms`,
    });
    // A fragment when fixed, so the live site's DOM is unchanged.
    const Frame = inline ? InlineNavFrame : React.Fragment;
    return (_jsxs(Dialog.Root, { open: open, onOpenChange: setOpen, children: [_jsxs(Frame, { children: [inline && archShadow && (_jsx("div", { "aria-hidden": "true", className: cn("pointer-events-none absolute top-[-10px] left-1/2 h-[94px] w-[min(var(--nav-desktop-width,1232px),calc(100%-48px))] -translate-x-1/2", barResponsiveInlineClass, linksOverflow && "w-full"), 
                        // The shadow is cast by the PARENT of the masked layer, never by
                        // the masked layer itself: masking runs after filtering, so a
                        // drop-shadow on the same element is erased by the very mask that
                        // gives it its shape. Nothing here paints, so this box needs no
                        // clip of its own — the child trims the lead-in.
                        style: {
                            filter: `drop-shadow(${archShadow.shadow})`,
                            "--nav-desktop-width": `${desktopWidth}px`,
                        }, children: _jsx("div", { className: cn("absolute inset-0", clipTopLead, 
                            // Below 481px the bar itself drops its mask for a flat band
                            // (see barResponsiveInlineClass), so the shape casting the
                            // shadow has to go flat with it. `!` beats the inline mask:
                            // an important author declaration outranks a non-important
                            // inline style, which is the same trick the bar uses.
                            "@max-[481px]/navbar:[mask:none]! @max-[481px]/navbar:[-webkit-mask:none]!"), style: {
                                background: archShadow.fill,
                                // Nominal size, stretched to the box. The bar's own mask is
                                // re-measured so its shoulders keep a constant width; this
                                // one's drift over a narrower frame is a few pixels under a
                                // soft blur, and invisible.
                                maskImage: navArchMask(INITIAL_NAV_ARCH_SIZE.w, INITIAL_NAV_ARCH_SIZE.h),
                                WebkitMaskImage: navArchMask(INITIAL_NAV_ARCH_SIZE.w, INITIAL_NAV_ARCH_SIZE.h),
                                maskSize: "100% 100%",
                                WebkitMaskSize: "100% 100%",
                                maskMode: "alpha",
                            } }) })), _jsxs(NavRefract, { ...props, style: {
                            ...style,
                            "--nav-desktop-width": `${desktopWidth}px`,
                        }, elementRef: (element) => {
                            navRef.current = element;
                            if (typeof forwardedRef === "function")
                                forwardedRef(element);
                            else if (forwardedRef)
                                forwardedRef.current = element;
                        }, frost: 7, 
                        /*
                          No liquid lens inline. The displacement map shifts pixels up to
                          72px near the rim with chromatic dispersion, which is gorgeous
                          over the site's aurora and useless over the canvas's flat chrome
                          ground — there is nothing there to refract. Worse, it smears
                          anything WITH contrast near the rim, so an arch shadow came back
                          as a metallic streak along both shoulders. Off also means the
                          canvas stops regenerating a displacement map on every resize.
                        */
                        refract: !inline, rimBreakpoint: inline ? "container" : "viewport", radius: 30, refraction: 90, depth: 16, dispersion: 100, "data-compact": collapsed ? "" : undefined, "data-links-overflow": linksOverflow ? "" : undefined, className: cn("isolate flex h-[94px] w-[min(var(--nav-desktop-width,1232px),calc(100%-48px))] -translate-x-1/2 items-center justify-between px-[130px] pt-[10px] font-sans text-white", 
                        // Absolute against InlineNavFrame rather than fixed against the
                        // viewport. Same -10px offset, so the same 10px of arch shoulder and
                        // rim stroke is clipped — by the frame instead of the viewport edge.
                        inline
                            ? cn("absolute top-[-10px] left-1/2 z-[1]", clipTopLead)
                            : "fixed top-[-10px] left-1/2 z-[80]", "bg-[linear-gradient(90deg,rgba(44,62,129,0.9)_0%,rgba(0,102,172,0.9)_100%)]", "transition-[width] duration-550 ease-[cubic-bezier(0.215,0.61,0.355,1)] motion-reduce:transition-none", inline ? barResponsiveInlineClass : barResponsiveClass, collapsed && "min-[1161px]:w-[360px] delay-250", linksOverflow && "w-full transition-none", className), onMouseEnter: (event) => {
                            pointerWithin.current = true;
                            pointerPosition.current = { x: event.clientX, y: event.clientY };
                            if (compact)
                                setExpanded(true);
                            onMouseEnter?.(event);
                        }, onMouseLeave: (event) => {
                            pointerWithin.current = false;
                            if (!focusWithin.current)
                                setExpanded(false);
                            clearPill();
                            onMouseLeave?.(event);
                        }, onFocusCapture: (event) => {
                            focusWithin.current = true;
                            if (compact)
                                setExpanded(true);
                            onFocusCapture?.(event);
                        }, onBlurCapture: (event) => {
                            if (!event.currentTarget.contains(event.relatedTarget)) {
                                focusWithin.current = false;
                                if (!pointerWithin.current)
                                    setExpanded(false);
                                clearPill();
                            }
                            onBlurCapture?.(event);
                        }, children: [_jsx(LinkAs, { href: brandHref, onFocus: clearPill, inert: collapsed, onClick: close, className: cn("relative z-[1] inline-flex shrink-0 items-center gap-3 transition-[opacity,transform,visibility] duration-200 motion-reduce:transition-none", focusClass, collapsed
                                    ? "invisible -translate-y-2.5 opacity-0 delay-0"
                                    : "delay-[600ms]"), children: _jsx("span", { ref: brandRef, className: "inline-flex shrink-0", children: brand }) }), _jsxs("nav", { id: linksId, "aria-label": "Primary", inert: collapsed || linksOverflow, "aria-hidden": linksOverflow || undefined, className: cn("ml-auto hidden items-center", 
                                // Hide the desktop subtree in the same render that shows the mobile trigger.
                                // Opacity preserves the measurements needed to return to desktop.
                                linksOverflow &&
                                    "pointer-events-none invisible opacity-0 absolute right-[130px]", inline ? "@min-[1161px]/navbar:flex" : "min-[1161px]:flex"), children: [_jsx("div", { ref: desktopRowRef, className: cn(inline ? rowInlineClass : rowClass, "absolute top-0 z-[1]"), onMouseLeave: () => {
                                            if (!focusWithin.current)
                                                clearPill();
                                        }, children: links.map((link, index) => (_jsx(LinkAs, { href: link.href, ...externalLinkProps(link.href), "aria-current": link.active ? "page" : undefined, onClick: close, onMouseEnter: () => showPill(index), onFocus: () => showPill(index), style: motionStyle(index), className: cn("relative -mx-[24px] inline-flex h-full items-center px-[24px] whitespace-nowrap transition-[opacity,visibility] duration-200 motion-reduce:transition-none", 
                                            // Fixed layout paints the readable labels in the crisp row
                                            // below, over the bar's own backdrop filter; these are only
                                            // the hit targets. Inline has no such row, so they are the
                                            // labels.
                                            inline ? "text-white" : "text-transparent", focusClass, collapsed && "invisible opacity-0"), children: _jsxs("span", { className: "relative", ref: (element) => {
                                                    linkRefs.current[index] = element;
                                                }, children: [inline && (link.active || hovered === index) && (_jsx("span", { "aria-hidden": "true", className: "marketing-nav-hover-pill pointer-events-none absolute -inset-x-[24px] top-1/2 -z-10 h-[38px] -translate-y-1/2" })), link.label, isExternalLink(link.href) && (_jsx(ExternalLinkIcon, { className: "ml-2 inline-block align-baseline" }))] }) }, link.href))) }), _jsx("div", { ref: actionsRef, onFocusCapture: clearPill, className: cn("relative z-[1] flex items-center gap-2 transition-none", collapsed && "invisible opacity-0"), children: contact })] }), _jsx("button", { type: "button", "aria-label": "Expand navigation", "aria-controls": linksId, "aria-expanded": !collapsed, onClick: () => setExpanded(true), className: cn(linksOverflow && "hidden!", "absolute inset-0 hidden cursor-pointer items-center justify-center text-[18px] font-medium transition-none", inline
                                    ? "@min-[1161px]/navbar:inline-flex"
                                    : "min-[1161px]:inline-flex", focusClass, collapsed
                                    ? "opacity-100"
                                    : "pointer-events-none invisible opacity-0"), children: "Menu" }), _jsx(Dialog.Trigger, { asChild: true, children: _jsx("button", { type: "button", "aria-label": "Open menu", className: cn(linksOverflow && "inline-flex!", "relative z-[1] -m-2 ml-auto inline-flex h-11 w-11 cursor-pointer items-center justify-center text-white", inline
                                        ? "@min-[1161px]/navbar:hidden"
                                        : "min-[1161px]:hidden", focusClass), children: _jsx("span", { className: "flex h-[25px] w-9 flex-col justify-between", "aria-hidden": "true", children: [0, 1, 2].map((i) => (_jsx("span", { className: "h-[3px] w-full rounded-[3px] bg-current" }, i))) }) }) })] })] }), !inline && !linksOverflow && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": "true", style: pill
                            ? { left: pill.left, top: pill.top, width: pill.width }
                            : undefined, className: cn("marketing-nav-hover-pill pointer-events-none fixed z-[81] h-[38px] max-[1161px]:hidden", pillMoves
                            ? "transition-[left,top,width,opacity,scale] duration-400 ease-[cubic-bezier(0.215,0.61,0.355,1)]"
                            : "transition-[opacity,scale] duration-400 ease-[cubic-bezier(0.215,0.61,0.355,1)]", "motion-reduce:transition-none", hovered !== null && linksRevealed && !collapsed && !open
                            ? "scale-100 opacity-100"
                            : "scale-50 opacity-0") }), _jsx("div", { "aria-hidden": "true", className: cn(rowClass, "pointer-events-none z-[82] text-white"), children: links.map((link, index) => (_jsxs("span", { style: motionStyle(index), className: cn("relative whitespace-nowrap transition-[opacity,visibility] duration-200 motion-reduce:transition-none", collapsed && "invisible opacity-0"), children: [link.active && (_jsx("span", { className: "marketing-nav-hover-pill absolute -inset-x-[24px] top-1/2 -z-10 h-[38px] -translate-y-1/2" })), _jsxs("span", { className: cn("inline-block transition-[opacity,filter] duration-200 motion-reduce:transition-none", linksRevealed &&
                                        !collapsed &&
                                        hovered !== null &&
                                        hovered !== index &&
                                        !link.active &&
                                        "opacity-[0.78] blur-[1px]"), children: [link.label, isExternalLink(link.href) && (_jsx(ExternalLinkIcon, { className: "ml-2 inline-block align-baseline" }))] })] }, link.href))) })] })), _jsxs(Dialog.Portal, { children: [_jsx(Dialog.Overlay, { className: "motion-safe:data-[state=open]:animate-[marketing-nav-overlay-in_320ms_both] motion-safe:data-[state=closed]:animate-[marketing-nav-overlay-out_320ms_both] fixed inset-0 z-[99] bg-[linear-gradient(90deg,rgba(44,62,129,0.9)_0%,rgba(0,102,172,0.9)_100%)] backdrop-blur-[28px] backdrop-saturate-[1.3]" }), _jsxs(Dialog.Content, { "aria-describedby": undefined, className: "motion-safe:data-[state=open]:animate-[marketing-nav-overlay-in_320ms_both] motion-safe:data-[state=closed]:animate-[marketing-nav-overlay-out_320ms_both] fixed inset-0 z-[100] flex h-dvh flex-col overflow-hidden px-16 pt-[calc(env(safe-area-inset-top,0px)+32px)] font-sans text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] outline-none max-[360px]:px-8", children: [_jsx(Dialog.Title, { className: "sr-only", children: "Navigation menu" }), _jsxs("div", { className: "flex h-8 shrink-0 items-center justify-between", children: [_jsx(LinkAs, { href: brandHref, className: cn("inline-flex items-center gap-3", focusClass), onClick: close, children: brand }), _jsx(Dialog.Close, { asChild: true, children: _jsxs("button", { type: "button", "aria-label": "Close menu", className: cn("relative -m-2 h-11 w-11 cursor-pointer text-white", focusClass), children: [_jsx("span", { "aria-hidden": "true", className: "absolute top-1/2 left-1 h-0.5 w-[34px] rotate-45 rounded-sm bg-current" }), _jsx("span", { "aria-hidden": "true", className: "absolute top-1/2 left-1 h-0.5 w-[34px] -rotate-45 rounded-sm bg-current" })] }) })] }), _jsxs("div", { "data-lenis-prevent": true, className: "mt-8 flex min-h-0 flex-1 flex-col items-start overflow-y-auto overscroll-contain pt-8 pb-[calc(env(safe-area-inset-bottom,0px)+96px)] [scrollbar-width:none]", children: [_jsx("nav", { "aria-label": "Primary mobile", className: "flex flex-col items-start gap-5", children: overlayLinks.map((link, index) => (_jsx(LinkAs, { href: link.href, ...externalLinkProps(link.href), "aria-current": link.active ? "page" : undefined, onClick: close, style: { animationDelay: `${60 + index * 50}ms` }, className: cn("group/mobile-link text-[42px] leading-normal font-light transition-colors duration-300 hover:text-[#D2E7F5] focus-visible:text-[#D2E7F5] motion-safe:animate-[marketing-nav-link-in_400ms_both] motion-reduce:transition-none", link.active && "text-[#D2E7F5]", focusClass), children: _jsxs("span", { className: "pointer-events-none inline-block transition-transform duration-300 group-hover/mobile-link:-translate-y-0.5 group-focus-visible/mobile-link:-translate-y-0.5 motion-reduce:transform-none motion-reduce:transition-none", children: [link.label, isExternalLink(link.href) && (_jsx(ExternalLinkIcon, { className: "ml-2 inline-block align-baseline" }))] }) }, link.href))) }), actions && (_jsx("div", { onClick: close, className: "mt-8 flex flex-wrap gap-3", children: actions })), renderedMenuFooter && (_jsx("div", { className: "mt-12", children: renderedMenuFooter }))] })] })] })] }));
});
MarketingNav.displayName = "MarketingNav";
export { MarketingNav };
//# sourceMappingURL=marketing-nav.js.map